/**
 * Gallery — calm photo keepsake.
 *
 * Pulls moments for the selected child, groups them by year-month, and renders
 * a soft tiled keepsake. Builds entirely on the existing /api/moments endpoint
 * + /api/files download (with JWT in query string).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChild } from "../lib/childContext";
import { api, API } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Brand from "../components/Brand";
import SafeImage from "../components/SafeImage";
import { ArrowLeft, ImageIcon } from "lucide-react";

const MONTH_LABEL = (key) => {
  // key = "2026-02"
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

function authedSrc(url, token) {
  if (!url || !token) return url;
  if (url.includes("?auth=")) return url;
  return url + (url.includes("?") ? "&" : "?") + "auth=" + encodeURIComponent(token);
}

export default function Gallery() {
  const { selected } = useChild();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [moments, setMoments] = useState(null);
  const token =
    typeof window !== "undefined" ? localStorage.getItem("orbit_token") : null;

  useEffect(() => {
    if (!selected?.id) return;
    let alive = true;
    api
      .get(`/children/${selected.id}/moments`)
      .then((r) => {
        if (alive) setMoments(r.data || []);
      })
      .catch(() => alive && setMoments([]));
    return () => {
      alive = false;
    };
  }, [selected?.id]);

  // Group by year-month
  const grouped = (moments || []).reduce((acc, m) => {
    const dt = new Date(m.created_at);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});
  const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  if (!selected) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#525252]">
        Please choose a child first.
      </div>
    );
  }

  return (
    <div data-testid="gallery-screen" className="min-h-screen">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/more")}
          data-testid="gallery-back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29]"
          aria-label={t("common.back")}
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">More</span>
        </button>
        <Brand size="sm" dataTestId="brand-gallery" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-3xl mx-auto fade-up">
        <span className="eyebrow">{selected.name}</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight leading-none">
          {t("gallery.title")}
        </h1>
        <p className="mt-4 text-[#525252] text-sm">{t("gallery.subtitle")}</p>

        {moments === null ? (
          <div className="mt-10 text-[#A3A3A3]">{t("common.loading")}</div>
        ) : moments.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="mt-10 rounded-3xl border hairline bg-[#F2EFEA]/60 p-10 text-center"
          >
            <ImageIcon
              size={28}
              strokeWidth={1.3}
              className="mx-auto text-[#8A9984]"
            />
            <p className="mt-4 text-[#525252] text-sm italic">{t("gallery.empty")}</p>
          </div>
        ) : (
          <div className="mt-10 space-y-12">
            {sortedKeys.map((monthKey) => {
              const items = grouped[monthKey];
              const withPhotos = items.filter((m) => m.photo_url);
              return (
                <section
                  key={monthKey}
                  data-testid={`gallery-month-${monthKey}`}
                  className="space-y-4"
                >
                  <div className="flex items-baseline justify-between">
                    <h2 className="font-serif text-2xl text-[#171717]">
                      {MONTH_LABEL(monthKey)}
                    </h2>
                    <span className="text-xs text-[#8A9984]">
                      {t("gallery.month_count", { n: items.length })}
                    </span>
                  </div>
                  {withPhotos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {withPhotos.map((m) => {
                        const fullUrl = m.photo_url?.startsWith("http")
                          ? m.photo_url
                          : `${API}${m.photo_url}`;
                        return (
                          <a
                            key={m.id}
                            href={authedSrc(fullUrl, token)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`gallery-photo-${m.id}`}
                            className="block aspect-square rounded-2xl overflow-hidden bg-[#F2EFEA] hover:opacity-90 transition-opacity"
                          >
                            <SafeImage
                              src={authedSrc(fullUrl, token)}
                              alt={m.text || "moment"}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          </a>
                        );
                      })}
                    </div>
                  )}
                  <ul className="space-y-2">
                    {items.map((m) => (
                      <li
                        key={"text-" + m.id}
                        className="text-sm text-[#525252] border-l-2 border-[#9BA995]/40 pl-3"
                      >
                        <span className="italic">"{m.text}"</span>
                        <span className="text-xs text-[#8A9984] ml-2">
                          · {new Date(m.created_at).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
